import { Loader2 } from "lucide-react";

export const OHC_LOGO = "https://ohcampus.com/assets/images/logo/logo.png";

export function inr(n) {
  if (n == null || n === "") return "₹0";
  return "₹" + Number(n).toLocaleString("en-IN");
}

export function fmtDate(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "—"; }
}

export function fmtDateTime(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
}

export function timeAgo(s) {
  if (!s) return "—";
  const diff = (Date.now() - new Date(s).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export function PageHeader({ title, subtitle, children, testid }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6" data-testid={testid}>
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-[#312E81]">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-2 flex-wrap">{children}</div>}
    </div>
  );
}

export function Loading({ label = "Loading" }) {
  return (
    <div className="flex items-center justify-center py-24 text-slate-400 gap-2">
      <Loader2 className="w-5 h-5 animate-spin" /> {label}…
    </div>
  );
}

export function EmptyState({ title = "Nothing here yet", subtitle = "", icon: Icon }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      {Icon && <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-4"><Icon className="w-7 h-7 text-slate-400" /></div>}
      <p className="font-heading font-semibold text-slate-700">{title}</p>
      {subtitle && <p className="text-sm text-slate-400 mt-1 max-w-sm">{subtitle}</p>}
    </div>
  );
}

const STAGE_COLORS = {
  New: "bg-blue-50 text-blue-700 border-blue-200",
  Assigned: "bg-blue-50 text-blue-700 border-blue-200",
  Contacted: "bg-indigo-50 text-indigo-700 border-indigo-200",
  Connected: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Qualified: "bg-teal-50 text-teal-700 border-teal-200",
  Counselling: "bg-violet-50 text-violet-700 border-violet-200",
  "Course Shortlisted": "bg-amber-50 text-amber-700 border-amber-200",
  "College Shortlisted": "bg-amber-50 text-amber-700 border-amber-200",
  "Application Started": "bg-orange-50 text-orange-700 border-orange-200",
  "Admission Pending": "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
  Admitted: "bg-green-100 text-green-800 border-green-300",
  Lost: "bg-rose-50 text-rose-700 border-rose-200",
  Nurture: "bg-slate-100 text-slate-600 border-slate-200",
  Hot: "bg-rose-50 text-rose-700 border-rose-200",
  Warm: "bg-amber-50 text-amber-700 border-amber-200",
  Cold: "bg-sky-50 text-sky-700 border-sky-200",
  Invalid: "bg-slate-100 text-slate-500 border-slate-200",
  Critical: "bg-rose-600 text-white border-rose-600",
  High: "bg-orange-100 text-orange-800 border-orange-300",
  Normal: "bg-slate-100 text-slate-600 border-slate-200",
  Low: "bg-slate-50 text-slate-400 border-slate-200",
  Pending: "bg-amber-50 text-amber-700 border-amber-200",
  Completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Fully Paid": "bg-green-100 text-green-800 border-green-300",
  "Partially Paid": "bg-amber-50 text-amber-700 border-amber-200",
  "Not Paid": "bg-rose-50 text-rose-700 border-rose-200",
  Interested: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Not Interested": "bg-rose-50 text-rose-700 border-rose-200",
  Callback: "bg-orange-50 text-orange-700 border-orange-200",
  "Converted to Lead": "bg-green-100 text-green-800 border-green-300",
};

export function StatusBadge({ value }) {
  const cls = STAGE_COLORS[value] || "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${cls}`}>
      {value}
    </span>
  );
}

export function ScoreBadge({ score }) {
  let cls = "bg-sky-100 text-sky-700";
  if (score >= 75) cls = "bg-rose-100 text-rose-700";
  else if (score >= 45) cls = "bg-amber-100 text-amber-700";
  return <span className={`inline-flex items-center justify-center min-w-[2.2rem] px-2 py-0.5 rounded-md text-xs font-bold font-mono ${cls}`}>{score}</span>;
}

export function initials(name = "") {
  return name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
}

const AV_COLORS = ["bg-indigo-100 text-indigo-700", "bg-emerald-100 text-emerald-700",
  "bg-amber-100 text-amber-700", "bg-rose-100 text-rose-700", "bg-sky-100 text-sky-700",
  "bg-violet-100 text-violet-700", "bg-teal-100 text-teal-700", "bg-fuchsia-100 text-fuchsia-700"];

export function Avatar({ name = "?", size = 28 }) {
  const idx = (name.charCodeAt(0) || 0) % AV_COLORS.length;
  return (
    <span className={`inline-flex items-center justify-center rounded-full font-semibold shrink-0 ${AV_COLORS[idx]}`}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.38) }}>{initials(name)}</span>
  );
}

export function AvatarStack({ names = [], max = 3 }) {
  const shown = names.slice(0, max);
  return (
    <div className="flex -space-x-2">
      {shown.map((n, i) => <span key={i} className="ring-2 ring-white rounded-full"><Avatar name={n} size={26} /></span>)}
      {names.length > max && <span className="w-[26px] h-[26px] rounded-full bg-slate-100 text-slate-500 text-[10px] font-semibold flex items-center justify-center ring-2 ring-white">+{names.length - max}</span>}
    </div>
  );
}

export function Ring({ value = 0, size = 46 }) {
  const r = (size - 8) / 2, c = 2 * Math.PI * r, off = c * (1 - Math.min(value, 100) / 100);
  const color = value >= 80 ? "#16a34a" : value >= 50 ? "#312E81" : value > 0 ? "#f59e0b" : "#cbd5e1";
  return (
    <svg width={size} height={size} className="shrink-0">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth="4" />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="4"
        strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" fontSize={Math.round(size * 0.26)} fontWeight="700" fill={color}>{value}%</text>
    </svg>
  );
}
